import type { Component } from "svelte";
import type { QueryClient } from "@tanstack/svelte-query";

/**
 * Base props interface that all islands must extend
 *
 * Islands should define their own Props interface that extends this:
 *
 * @example
 * ```typescript
 * interface Props extends IslandProps {
 *   myCustomProp: string;
 * }
 * ```
 */
export interface IslandProps {
  /**
   * The island's registered name, injected by bootstrap.ts at mount time.
   * Available for debugging, logging, or conditional logic.
   *
   * @example
   * ```typescript
   * let { name, queryClient }: Props = $props();
   * console.log(`[${name}] Mounted`);
   * ```
   */
  name?: string;

  /**
   * Shared Tanstack Query QueryClient injected by bootstrap.ts at mount time.
   *
   * Pass to `<QueryClientProvider client={queryClient}>` to make the shared
   * cache available to child components via createQuery() and useQueryClient().
   *
   * Using the injected instance (rather than constructing a new one) ensures
   * all islands share a single query cache, enabling cross-island cache
   * deduplication and invalidation.
   */
  queryClient: QueryClient;
}

/**
 * Type for a dynamically imported island module
 */
export type IslandModule<TProps extends IslandProps = IslandProps> = {
  default: Component<TProps>;
};

/**
 * Island registration entry
 *
 * Used in the island registry to map island names to their components
 */
export interface IslandRegistration {
  /**
   * The name used in data-island attributes
   * Must be unique across all islands
   *
   * @example "plp", "badge", "product-card"
   */
  name: string;

  /**
   * Lazy importer function for the island component
   * Enables code-splitting and lazy-loading
   *
   * We allow `any` here to allow islands to define their own props,
   * provided they implement the IslandProps interface for required
   * parameters.
   *
   * @see registerIsland - Type-safe registration helper
   * @example () => import("./plp/PlpIsland.svelte")
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importer: () => Promise<IslandModule<any>>;
}

/**
 * Type-safe helper for registering islands
 *
 * This helper MUST be used when registering islands in the registry.
 * It provides type checking to ensure the imported component matches
 * the expected prop shape.
 *
 * @param config - Island registration configuration
 * @returns IslandRegistration entry for the registry
 */
export function registerIsland<TProps extends IslandProps>(config: {
  name: string;
  importer: () => Promise<IslandModule<TProps>>;
}): IslandRegistration {
  return config;
}
